import type { Session, User } from '@supabase/supabase-js';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { supabase } from '../lib/supabase';

/**
 * How often to re-ask for the session while the server is unreachable.
 *
 * auth-js runs its own refresh ticker (~30s) and would eventually emit
 * TOKEN_REFRESHED on its own, which the listener below picks up. This only
 * shortens the wait for someone actually staring at the reconnecting screen.
 */
const RETRY_INTERVAL_MS = 10_000;

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  loading: boolean;
  /**
   * `session` is null because we could not REACH the auth server — not because
   * the user is signed out.
   *
   * This is the difference between "your session expired" and "your phone lost
   * signal for a moment", and conflating them is what made the installed PWA
   * feel like it logged students out constantly. Sequence: the app is
   * backgrounded for over an hour, so the access token expires; on reopen
   * auth-js reads the still-valid refresh token from storage and tries to
   * exchange it; on a flaky mobile connection that fetch fails, and
   * `getSession()` reports `{ session: null, error }`. A genuinely signed-out
   * visitor gets `{ session: null, error: null }` with no network call at all —
   * so the error is what separates "unknown" from "signed out". auth-js
   * deliberately KEEPS the refresh token on disk for a retryable failure, so
   * nothing is lost; the guards must wait rather than redirect to /login.
   */
  unreachable: boolean;
  /** Re-ask for the session now. Wired to the reconnecting screen's button. */
  retry: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [unreachable, setUnreachable] = useState(false);
  const cancelled = useRef(false);

  const loadSession = useCallback(async (): Promise<void> => {
    try {
      const { data, error } = await supabase.auth.getSession();
      if (cancelled.current) return;
      if (data.session === null && error !== null) {
        // Unknown, not signed out — see the `unreachable` doc above. Leave the
        // previously known session in place: a refresh that fails mid-session
        // must not tear down the screen the user is on.
        setUnreachable(true);
      } else {
        setSession(data.session);
        setUnreachable(false);
      }
    } catch {
      // A rejection (rather than an `error` result) means storage or the
      // network layer itself misbehaved. Also unknown, never a sign-out.
      if (cancelled.current) return;
      setUnreachable(true);
    } finally {
      if (!cancelled.current) setLoading(false);
    }
  }, []);

  const retry = useCallback((): void => {
    void loadSession();
  }, [loadSession]);

  useEffect(() => {
    cancelled.current = false;
    void loadSession();

    const { data: sub } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (event === 'SIGNED_OUT') {
        setSession(null);
        setUnreachable(false);
        setLoading(false);
        return;
      }
      if (nextSession !== null) {
        setSession(nextSession);
        setUnreachable(false);
        setLoading(false);
      }
      // A null session on any OTHER event is not proof of a sign-out —
      // INITIAL_SESSION fires with null while a refresh is still failing.
      // `loadSession` is the only thing allowed to conclude "signed out",
      // because it is the only one that sees the accompanying error.
    });

    return () => {
      cancelled.current = true;
      sub.subscription.unsubscribe();
    };
  }, [loadSession]);

  // While unreachable, retry on the two signals that actually mean "the network
  // may be back" on a phone — regaining connectivity, and the PWA being brought
  // back to the foreground — plus a slow poll for everything else.
  useEffect(() => {
    if (!unreachable) return undefined;

    const onVisible = (): void => {
      if (document.visibilityState === 'visible') retry();
    };

    window.addEventListener('online', retry);
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(retry, RETRY_INTERVAL_MS);

    return () => {
      window.removeEventListener('online', retry);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [unreachable, retry]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      unreachable,
      retry,
    }),
    [session, loading, unreachable, retry],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
